import { getEnvironmentConstant } from './utilities';
import { CONTENTFUL_SPACE_ID, CONTENTFUL_ENVIRONMENT_ID } from './constants';

// Fast Track status constants
export const FAST_TRACK_STATUS = {
  UNASSIGNED: 'unassigned',
  IN_PROGRESS: 'in_progress',
  CANCELLED: 'cancelled',
};

// Content item type constants
export const CONTENT_ITEM_TYPES = {
  CONTENT_ITEMS_GROUP: 'ContentItemsGroup',
  LEARN_LIVE_CLASS: 'LearnLiveClass',
  LEARN_LIVE_COURSE: 'LearnLiveCourse',
  MODULE: 'Module',
  COURSE: 'Course',
};

/**
 * Determines if a learning plan was created from a FastTrack template
 * @param {object} learningPlan - The learning plan object
 * @returns {boolean} - True if the plan is from a FastTrack template, false otherwise
 */
export const isFastTrackLearningPlan = (learningPlan) => {
  if (!learningPlan || !learningPlan.entityType) {
    return false;
  }
  const entityType = learningPlan.entityType.toLowerCase();
  return entityType === 'fasttrack' || entityType === 'fast track';
};

/**
 * Determines if a content item is a ContentItemsGroup (has nested contentItems)
 * @param {object} contentItem - The content item to check
 * @returns {boolean} - True if the item is a ContentItemsGroup, false otherwise
 */
export const isContentItemsGroup = (contentItem) => {
  if (!contentItem || !contentItem.type) {
    return false;
  }
  return contentItem.type.toLowerCase() === CONTENT_ITEM_TYPES.CONTENT_ITEMS_GROUP.toLowerCase();
};

/**
 * Determines if a content item is a LearnLiveClass
 * @param {object} contentItem - The content item to check
 * @returns {boolean} - True if the item is a LearnLiveClass, false otherwise
 */
export const isLearnLiveClass = (contentItem) => {
  if (!contentItem || !contentItem.type || typeof contentItem.type !== 'string') {
    return false;
  }
  return contentItem.type.toLowerCase() === CONTENT_ITEM_TYPES.LEARN_LIVE_CLASS.toLowerCase();
};

/**
 * Determines if a content item is a LearnLiveCourse
 * @param {object} contentItem - The content item to check
 * @returns {boolean} - True if the item is a LearnLiveCourse, false otherwise
 */
export const isLearnLiveCourse = (contentItem) => {
  if (!contentItem || !contentItem.type) {
    return false;
  }
  return contentItem.type.toLowerCase() === CONTENT_ITEM_TYPES.LEARN_LIVE_COURSE.toLowerCase();
};

/**
 * Determines if a content item is a coaching session (for backwards compatibility)
 * @param {object} contentItem - The content item to check
 * @returns {boolean} - True if the item is a coaching session, false otherwise
 */
export const isCoachingSession = (contentItem) => {
  if (!contentItem || !contentItem.objectType) {
    return false;
  }

  const objectType = contentItem.objectType.toLowerCase();
  const learningMode = contentItem.learningMode?.toLowerCase();

  return (
    objectType === 'coaching'
    || objectType === 'session'
    || objectType === 'coaching_session'
    || learningMode === 'coaching'
    || learningMode === 'live_coaching'
    || isLearnLiveClass(contentItem)
  );
};

export const formatDateForFastTrack = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats a date/time string for display with proper timezone handling
 * @param {string} dateTimeString - The date/time string to format
 * @param {string} format - The date format to use (default: 'mmm d, yyyy h:MM TT')
 * @param {string} timezone - The timezone to use (default: user's local timezone)
 * @returns {string} - Formatted date string
 */

export const formatDateTime = (startDateTime, endDateTime) => {
  if (!startDateTime || !endDateTime) return '';
  const startDateTimeObject = new Date(startDateTime);
  const endDateTimeObject = new Date(endDateTime);

  // Check if dates are valid
  if (Number.isNaN(startDateTimeObject.getTime()) || Number.isNaN(endDateTimeObject.getTime())) {
    return '';
  }

  const startDate = formatDateForFastTrack(startDateTime);
  const endDate = formatDateForFastTrack(endDateTime);
  const startTime = startDateTimeObject.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const endTime = endDateTimeObject.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  if (startDate === endDate) {
    return `${startDate} from ${startTime}-${endTime}`;
  }
  return `${startDate} ${startTime}-${endDate} ${endTime}`;
};

/**
 * Gets the class status display text
 * @param {object} classItem - The LearnLiveClass item
 * @returns {string} - Status display text
 */
export const getClassStatusDisplay = (classItem) => {
  if (!classItem || !classItem.classStatus) {
    return 'Unknown';
  }

  const status = classItem.classStatus.toLowerCase();
  switch (status) {
  case 'scheduled':
    return 'Scheduled';
  case 'completed':
    return 'Completed';
  case 'cancelled':
  case 'canceled':
    return 'Cancelled';
  case 'in_progress':
  case 'in-progress':
    return 'In Progress';
  default:
    return classItem.classStatus;
  }
};

/**
 * Gets attendance information for a class
 * @param {object} classItem - The LearnLiveClass item
 * @returns {object} - Attendance info { attended, enrolled, percentage }
 */
export const getAttendanceInfo = (classItem) => {
  if (!classItem) {
    return { attended: 0, enrolled: 0, percentage: 0 };
  }

  const attended = classItem.totalAttended || 0;
  const enrolled = classItem.totalEnrolled || 0;
  const percentage = enrolled > 0 ? Math.round((attended / enrolled) * 100) : 0;

  return { attended, enrolled, percentage };
};

/**
 * Determines the FastTrack status based on assigned users and session data
 * @param {object} learningPlan - The learning plan object
 * @returns {string} - FastTrack status (UNASSIGNED, IN_PROGRESS, or CANCELLED)
 */
export const getFastTrackStatus = (learningPlan) => {
  if (!isFastTrackLearningPlan(learningPlan)) {
    return null;
  }

  const assignedUsers = learningPlan.assignedUsers || [];
  const hasAssignedUsers = assignedUsers.length > 0;

  // Check for cancelled coaching sessions in content items
  const hasCancelledCoachingSessions = learningPlan.contentItems?.some(
    (item) => isCoachingSession(item)
      && (item.status === 'Canceled' || item.status === 'Cancelled'),
  );

  // If there are assigned users, plan is in progress
  if (hasAssignedUsers) {
    return FAST_TRACK_STATUS.IN_PROGRESS;
  }

  // If no assigned users but has cancelled sessions, still show as cancelled for those sessions
  if (hasCancelledCoachingSessions) {
    return FAST_TRACK_STATUS.CANCELLED;
  }

  // Default state is unassigned
  return FAST_TRACK_STATUS.UNASSIGNED;
};

/**
 * Flattens content items, extracting nested items from ContentItemsGroups
 * @param {Array} contentItems - Array of content items (may include groups)
 * @returns {Array} - Flattened array of all content items
 */
export const flattenContentItems = (contentItems) => {
  if (!contentItems || !Array.isArray(contentItems)) {
    return [];
  }

  const flattened = [];

  contentItems.forEach((item) => {
    if (isContentItemsGroup(item) && item.contentItems) {
      // Recursively flatten nested groups
      flattened.push(...flattenContentItems(item.contentItems));
    } else {
      flattened.push(item);
    }
  });

  return flattened;
};

/**
 * Gets instructor display name
 * @param {object} instructor - The instructor object
 * @returns {string} - Formatted instructor name
 */
export const getInstructorName = (instructor) => {
  if (!instructor) {
    return 'TBD';
  }

  const firstName = instructor.firstName || '';
  const lastName = instructor.lastName || '';

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  if (firstName) {
    return firstName;
  }

  if (lastName) {
    return lastName;
  }

  return 'TBD';
};

/**
 * Checks if a class has a valid start date/time
 * @param {object} classItem - The LearnLiveClass item
 * @returns {boolean} - True if has valid start date/time
 */
export const hasValidStartDateTime = (classItem) => {
  const startDateTime = classItem?.startDatetime;

  return Boolean(
    startDateTime !== ''
    && !Number.isNaN(Date.parse(startDateTime)),
  );
};

/**
 * Checks if a content item has a CONTENT_NOT_FOUND error
 * @param {object} contentItem - The content item to check
 * @returns {boolean} - True if the item has a CONTENT_NOT_FOUND error
 */
export const hasContentNotFoundError = (contentItem) => {
  if (!contentItem || !contentItem.error) {
    return false;
  }
  return contentItem.error.code === 'CONTENT_NOT_FOUND';
};

// Learning Records status constants
export const LEARNING_RECORD_VERBS = {
  VIEWED: 'viewed',
  STARTED: 'started',
  CONSUMED: 'consumed',
  COMPLETED: 'completed',
};

// Status constants for content items
export const CONTENT_ITEM_STATUS = {
  SCHEDULED: 'scheduled',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  IN_PROGRESS: 'in_progress',
  VIEWED: 'viewed',
  NOT_STARTED: 'not_started',
  NOT_ATTENDED: 'not_attended',
  ATTENDED: 'attended',
};

/**
 * Determines the status for a LearnLiveClass based on date/time and stillOffered flag
 * @param {object} classItem - The LearnLiveClass item
 * @returns {string} - Status string (scheduled, completed, cancelled, or not_started)
 */
export const getLearnLiveClassStatus = (classItem, attended) => {
  if (!classItem) {
    return CONTENT_ITEM_STATUS.NOT_STARTED;
  }

  const startDate = new Date(classItem.startDatetime);
  const endDate = new Date(classItem.endDatetime);
  const now = new Date();

  // Check if class is cancelled
  if (classItem.stillOffered === false) {
    return CONTENT_ITEM_STATUS.CANCELLED;
  }

  // Check if startDatetime is valid and in the future
  if (hasValidStartDateTime(classItem)) {
    if (startDate > now || endDate > now) {
      return CONTENT_ITEM_STATUS.SCHEDULED;
    }
  }

  if (endDate < now && attended !== undefined && attended === false) {
    return CONTENT_ITEM_STATUS.NOT_ATTENDED;
  }
  if (endDate < now && attended !== undefined && attended === true) {
    return CONTENT_ITEM_STATUS.ATTENDED;
  }
  if (endDate < now && hasValidStartDateTime(classItem)) {
    return CONTENT_ITEM_STATUS.COMPLETED;
  }

  // Default to not started if no valid dates
  return CONTENT_ITEM_STATUS.NOT_STARTED;
};

/**
 * Determines the status for a content item based on learning records
 * @param {object} contentItem - The content item
 * @param {object} learningRecords - The user's learning records
 * @param {object} env - The environment object
 * @returns {string} - Status string (completed, in_progress, viewed, or not_started)
 */
export const getContentItemStatusFromLearningRecords = (contentItem, learningRecords, env) => {
  if (!contentItem || !learningRecords) {
    return CONTENT_ITEM_STATUS.NOT_STARTED;
  }

  // Get the content item ID (could be id, uri, or other identifier)
  const contentId = contentItem.id || contentItem.uri || contentItem.contentId;
  if (!contentId) {
    return CONTENT_ITEM_STATUS.NOT_STARTED;
  }

  // Find the learning record for this content item
  // Try both the direct ID and the Contentful URL format
  let learningRecord = learningRecords[contentId];
  if (!learningRecord && contentItem.id && env) {
    // Try to find by Contentful URL format (this is what generateLRSID creates)
    const spaceId = getEnvironmentConstant({ key: CONTENTFUL_SPACE_ID, env });
    try {
      const environmentId = getEnvironmentConstant({ key: CONTENTFUL_ENVIRONMENT_ID, env });
      const contentfulUrl = `https://cdn.contentful.com/spaces/${spaceId}/environments/${environmentId}/entries/${contentItem.id}`;
      learningRecord = learningRecords[contentfulUrl];
    } catch (error) {
      // If environment constants fail, try common environments as fallback
      const fallbackEnvironments = ['stage', 'master', 'development'];

      // Use array methods instead of for...of loop
      fallbackEnvironments.some((envName) => {
        try {
          const contentfulUrl = `https://cdn.contentful.com/spaces/${spaceId}/environments/${envName}/entries/${contentItem.id}`;
          if (learningRecords[contentfulUrl]) {
            learningRecord = learningRecords[contentfulUrl];
            return true; // Break out of the loop
          }
        } catch (fallbackError) {
          // Silently continue to next environment
        }
        return false; // Continue to next environment
      });
    }
  }

  if (!learningRecord || !learningRecord.statements || !Array.isArray(learningRecord.statements)) {
    return CONTENT_ITEM_STATUS.NOT_STARTED;
  }

  const { statements } = learningRecord;

  // Check for completed status (consumed or completed verb)
  const hasCompleted = statements.some((statement) => statement.verb === LEARNING_RECORD_VERBS.CONSUMED
    || statement.verb === LEARNING_RECORD_VERBS.COMPLETED);

  if (hasCompleted) {
    return CONTENT_ITEM_STATUS.COMPLETED;
  }

  // Check for started status
  const hasStarted = statements.some((statement) => statement.verb === LEARNING_RECORD_VERBS.STARTED);

  if (hasStarted) {
    return CONTENT_ITEM_STATUS.IN_PROGRESS;
  }

  // Check for viewed status
  const hasViewed = statements.some((statement) => statement.verb === LEARNING_RECORD_VERBS.VIEWED);

  if (hasViewed) {
    return CONTENT_ITEM_STATUS.VIEWED;
  }

  return CONTENT_ITEM_STATUS.NOT_STARTED;
};

/**
 * Gets the overall status for any content item (handles both LearnLiveClass and other types)
 * @param {object} contentItem - The content item
 * @param {object} learningRecords - The user's learning records
 * @param {object} env - The environment object
 * @returns {string} - Status string
 */
export const getContentItemStatus = (contentItem, learningRecords, env, attended) => {
  if (!contentItem) {
    return CONTENT_ITEM_STATUS.NOT_STARTED;
  }

  // Handle LearnLiveClass differently
  if (isLearnLiveClass(contentItem)) {
    return getLearnLiveClassStatus(contentItem, attended);
  }

  // Handle other content types using learning records
  return getContentItemStatusFromLearningRecords(contentItem, learningRecords, env);
};

/**
 * Gets the display text for a status
 * @param {string} status - The status string
 * @returns {string} - Display text for the status
 */
export const getStatusDisplayText = (status) => {
  switch (status) {
  case CONTENT_ITEM_STATUS.SCHEDULED:
    return 'Scheduled';
  case CONTENT_ITEM_STATUS.COMPLETED:
    return 'Completed';
  case CONTENT_ITEM_STATUS.CANCELLED:
    return 'Cancelled';
  case CONTENT_ITEM_STATUS.IN_PROGRESS:
    return 'In Progress';
  case CONTENT_ITEM_STATUS.VIEWED:
    return 'Viewed';
  case CONTENT_ITEM_STATUS.NOT_STARTED:
    return 'Not Started';
  case CONTENT_ITEM_STATUS.ATTENDED:
    return 'Attended';
  case CONTENT_ITEM_STATUS.NOT_ATTENDED:
    return 'Did not attend';
  default:
    return status || 'Unknown';
  }
};

/**
 * Counts the total number of content items in a Fast Track learning plan
 * @param {Array} contentItems - Array of content items (may include groups)
 * @returns {number} - Total count of all content items including nested items
 */
export const getTotalItemsCount = (contentItems) => {
  if (!contentItems || !Array.isArray(contentItems)) {
    return 0;
  }

  const flattened = flattenContentItems(contentItems);
  return flattened.filter((item) => item.isRemoved !== true && item.type !== CONTENT_ITEM_TYPES.CONTENT_ITEMS_GROUP).length;
};

/**
 * Counts the number of completed content items in a Fast Track learning plan
 * @param {Array} contentItems - Array of content items (may include groups)
 * @param {object} learningRecords - The user's learning records
 * @param {object} env - The environment object
 * @returns {number} - Count of completed content items
 */
export const getCompletedItemsCount = (contentItems, learningRecords, env) => {
  if (!contentItems || !Array.isArray(contentItems)) {
    return 0;
  }

  const flattened = flattenContentItems(contentItems);

  return flattened.filter((item) => {
    const status = getContentItemStatus(item, learningRecords, env);
    return status === CONTENT_ITEM_STATUS.COMPLETED || status === CONTENT_ITEM_STATUS.ATTENDED || status === CONTENT_ITEM_STATUS.NOT_ATTENDED;
  }).length;
};

/**
 * Generates a unique identifier for calendar events
 * @param {string} classId - The class ID
 * @param {string} startDateTime - The start date/time string
 * @returns {string} - Unique identifier for the calendar event
 */
export const generateCalendarEventId = (classId, startDateTime) => {
  const timestamp = new Date(startDateTime).getTime();
  return `${classId}-${timestamp}@autodesk.com`;
};

/**
 * Formats a date for ICS format (YYYYMMDDTHHMMSSZ)
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export const formatDateForICS = (date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

/**
 * Escapes special characters in ICS content
 * @param {string} text - The text to escape
 * @returns {string} - Escaped text
 */
export const escapeICSText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
};

/**
 * Generates ICS content for a LearnLiveClass
 * @param {object} classData - The LearnLiveClass data
 * @returns {string} - ICS file content
 */
export const generateICSContent = (classData) => {
  if (!classData || !classData.startDatetime || !classData.endDatetime) {
    throw new Error('Invalid class data: missing required date/time information');
  }

  const startDate = new Date(classData.startDatetime);
  const endDate = new Date(classData.endDatetime);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    throw new Error('Invalid date format in class data');
  }

  const eventId = generateCalendarEventId(classData.id, classData.startDatetime);
  const now = new Date();
  const startFormatted = formatDateForICS(startDate);
  const endFormatted = formatDateForICS(endDate);
  const nowFormatted = formatDateForICS(now);

  // Build description with all relevant information
  let description = '';
  if (classData.parentCourse?.overview) {
    description += `Description: ${classData.parentCourse.overview}\n\n`;
  }
  if (classData.instructor) {
    description += `Instructor: ${getInstructorName(classData.instructor)}\n`;
  }
  if (classData.meetingPlatform) {
    description += `Platform: ${classData.meetingPlatform}\n`;
  }
  if (classData.meetingLink) {
    description += `Meeting Link: ${classData.meetingLink}\n`;
  }

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Autodesk//Learn One//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${eventId}`,
    `DTSTAMP:${nowFormatted}`,
    `DTSTART:${startFormatted}`,
    `DTEND:${endFormatted}`,
    `SUMMARY:${escapeICSText(classData.title)}`,
    `DESCRIPTION:${escapeICSText(description)}`,
    `LOCATION:${escapeICSText(classData.meetingPlatform || 'Online')}`,
    'STATUS:CONFIRMED',
    'TRANSP:OPAQUE',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return icsContent;
};

/**
 * Downloads an ICS file
 * @param {string} icsContent - The ICS file content
 * @param {string} filename - The filename for the download
 */
export const downloadICSFile = (icsContent, filename = 'calendar-event.ics') => {
  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || !window.Blob || !window.URL) {
      throw new Error('Browser environment required for file download');
    }

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  } catch (error) {
    throw new Error(`Failed to download calendar file: ${error.message}`);
  }
};
